import {
  Text,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  Primary13Regular,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'
import { BillingContactsPills } from 'modules/CustomerContacts'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { INTEGRATION_DETAILS, SmallIcon } from 'Integrations/index'
import { ReactNode } from 'react'
import { match } from 'ts-pattern'
import { inflect } from 'inflection'

const DATE_FORMAT = 'd LLLL yyyy'

const DetailItem = ({
  label,
  content,
  contentPaddingY = '8px',
  'data-testid': dataTestId
}: {
  label: ReactNode
  content?: ReactNode
  contentPaddingY?: string
  'data-testid'?: string
}) => {
  return (
    <Grid columnGap="16px" templateColumns="158px 1fr" data-testid={dataTestId}>
      <Box {...Primary13Regular} color={GreyGrey70} paddingY="8px">
        {label}
      </Box>
      <Box
        {...Primary13Regular}
        color={content ? GreyGrey80 : GreyGrey60}
        position="relative"
        paddingY={contentPaddingY}
      >
        {content ? content : 'Not set'}
      </Box>
    </Grid>
  )
}

const Customer = (props: { customerId?: string }) => {
  return (
    <DetailItem
      label="Customer"
      contentPaddingY={props.customerId ? '4px' : undefined}
      content={
        props.customerId ? (
          <CustomerPreviewCardPill customerId={props.customerId} />
        ) : undefined
      }
    />
  )
}

export const BillingContacts = (props: {
  contacts?: Array<{ id: string; email: string; name: string }>
}) => {
  if (!props.contacts) {
    return <DetailItem label="Billing contacts" />
  }

  return (
    <DetailItem
      label="Billing contacts"
      contentPaddingY="4px"
      content={<BillingContactsPills contacts={props.contacts} />}
    />
  )
}

export const StartDate = (props: { startDate?: Date }) => {
  return (
    <DetailItem
      label="Start date"
      content={
        props.startDate ? format(props.startDate, DATE_FORMAT) : undefined
      }
    />
  )
}

export const EndDate = (props: { endDate?: Date }) => {
  return (
    <DetailItem
      label="End date"
      content={props.endDate ? format(props.endDate, DATE_FORMAT) : undefined}
    />
  )
}

export const CancelledDate = (props: { cancelledDate?: Date }) => {
  return (
    <DetailItem
      data-testid="schedule.details.cancelledDate"
      label="Cancelled on"
      content={
        props.cancelledDate
          ? format(props.cancelledDate, DATE_FORMAT)
          : undefined
      }
    />
  )
}

export const PaymentMethod = (props: {
  paymentMethod: 'STRIPE' | 'GOCARDLESS'
}) => {
  return (
    <DetailItem
      data-testid="schedule.details.paymentMethod"
      label="Payment method"
      content={match(props.paymentMethod)
        .with('STRIPE', () => (
          <Flex gap="8px" alignItems="center">
            <SmallIcon
              url={INTEGRATION_DETAILS.Stripe?.smallLogoUrl as string}
            />
            Stripe payments
          </Flex>
        ))
        .with('GOCARDLESS', () => (
          <Flex gap="8px" alignItems="center">
            <SmallIcon
              url={INTEGRATION_DETAILS.GoCardless?.smallLogoUrl as string}
            />
            GoCardless payments
          </Flex>
        ))
        .exhaustive()}
    />
  )
}

export const AutoChargeCustomer = (props: { autoChargeCustomer?: boolean }) => {
  return (
    <DetailItem
      label="Auto-charge customer"
      content={
        <Flex gap="8px" alignItems="center">
          <SmallIcon url={INTEGRATION_DETAILS.Stripe?.smallLogoUrl as string} />
          {props.autoChargeCustomer ? 'Enabled' : 'Disabled'}
        </Flex>
      }
    />
  )
}

export const AutoIssueInvoices = (props: { autoIssueInvoices?: boolean }) => {
  return (
    <DetailItem
      label="Auto-send invoices"
      content={props.autoIssueInvoices ? 'Enabled' : 'Disabled'}
    />
  )
}

export const PurchaseOrderNumber = (props: {
  purchaseOrderNumber?: string
}) => {
  return <DetailItem label="PO number" content={props.purchaseOrderNumber} />
}

export const DefaultDueDateDays = (props: { defaultDueDateDays?: number }) => {
  return (
    <DetailItem
      label="Payment terms"
      content={
        typeof props.defaultDueDateDays === 'number'
          ? `${props.defaultDueDateDays} ${inflect('day', props.defaultDueDateDays)}`
          : undefined
      }
    />
  )
}

export const Memo = (props: { memo?: string }) => {
  return (
    <DetailItem
      label="Invoice memo"
      content={
        props.memo ? (
          <Text {...Primary13Regular} color="inherit" noOfLines={3}>
            {props.memo}
          </Text>
        ) : undefined
      }
    />
  )
}

export const Label = (props: { label?: string }) => {
  return <DetailItem label="Label" content={props.label} />
}

export const CustomField = (props: { fieldKey: string; value: string }) => {
  return (
    <DetailItem
      data-testid={`schedule.details.customField.${props.fieldKey}`}
      label={props.fieldKey}
      content={props.value || undefined}
    />
  )
}

export const ScheduleDetailItems = {
  Customer,
  BillingContacts,
  StartDate,
  EndDate,
  CancelledDate,
  PaymentMethod,
  AutoChargeCustomer,
  AutoIssueInvoices,
  PurchaseOrderNumber,
  Label,
  DefaultDueDateDays,
  Memo,
  CustomField
}
